import {
  selectFullPaymentPlansData,
  selectSelectedPaymentPlanId,
} from '@checkout-b/checkout/selectors/paymentPlans';
import { CurrencyContext } from '@checkout-c/CurrencyContext';
import { getJSFormattedCurrency } from '@checkout-h/currency';
import Translation from '@checkout-s/Translation';
import { useContext, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Fragment } from 'react/jsx-runtime';
import { PaymentPlanModal } from './PaymentPlanModal';
import { setPaymentPlan } from '@checkout-b/checkout/actions/paymentPlans';
import { GATEWAY_TYPE_SQUARE, GATEWAY_TYPE_STRIPE } from '@showclix-shared/Payment/constants/gatewayTypes';
import { getConfigBooleanValue } from '@showclix-shared-util/configClient';

const defaultPaymentOption = {
  paymentPlanId: -1,
  payInFull: true,
};

const PaymentPlanOption = ({
  paymentPlanId,
  numberOfInstallments,
  initialInstallmentAmount = {},
  selected,
  currencyConfig,
  payInFull = false,
  onOpenDetails,
  onSelect,
}) => {
  return (
    <div className="input__radio payment-plan-option">
      <div className="input__radio-wrap">
        <div className="input__radio__input">
          <input
            name="payment"
            type="radio"
            id={`payment-plan-${paymentPlanId}`}
            value={paymentPlanId}
            onChange={onSelect}
            checked={selected}
          />
        </div>
        <label
          id={`label-payment-plan-${paymentPlanId}`}
          className="input__radio__label"
          htmlFor={`payment-plan-${paymentPlanId}`}
        >
          <div className="input__radio__label-text">
            {payInFull && <Translation>{'checkout__pay_in_full'}</Translation>}
            {!payInFull && (
              <Fragment>
                <p className="payment-plan-option__title">
                  <Translation
                    interpolation={{
                      count: numberOfInstallments,
                    }}
                  >
                    {'checkout__pay_in_installments'}
                  </Translation>
                </p>
                <p className="payment-plan-option__description">
                  <Translation
                    interpolation={{
                      amount: getJSFormattedCurrency(initialInstallmentAmount.amount ?? 0, currencyConfig),
                    }}
                  >
                    {'checkout__pay_in_installments_description'}
                  </Translation>{' '}
                  <button
                    className="payment-plan-option__details-button button--link"
                    type="button"
                    onClick={onOpenDetails}
                  >
                    <Translation>{'checkout__pay_in_installments_full_details'}</Translation>
                  </button>
                </p>
              </Fragment>
            )}
          </div>
        </label>
      </div>
    </div>
  );
};

export const PaymentPlans = () => {
  const dispatch = useDispatch();

  const currencyConfig = useContext(CurrencyContext);

  if (!getConfigBooleanValue('paymentPlans', false)) {
    return null;
  }

  const gatewayType = useSelector((state) => state.payment.gatewayType);
  const paymentPlans = useSelector(selectFullPaymentPlansData);
  const selectedPaymentPlanId = useSelector(selectSelectedPaymentPlanId);

  const [paymentPlanIdOpened, setPaymentPlanIdOpened] = useState(null);

  const onOpenDetails = (paymentPlanId) => () => {
    setPaymentPlanIdOpened(paymentPlanId);
  };

  const onCloseDetails = () => {
    setPaymentPlanIdOpened(null);
  };

  const onPaymentPlanSelect = (paymentPlanId) => () => {
    dispatch(setPaymentPlan(paymentPlanId));
  };

  const openedPaymentPlan = useMemo(() => {
    return paymentPlans.find((plan) => plan.paymentPlanId === paymentPlanIdOpened);
  }, [paymentPlanIdOpened, paymentPlans]);

  const isValidGatewatType = [GATEWAY_TYPE_STRIPE, GATEWAY_TYPE_SQUARE].includes(Number(gatewayType));

  if (!paymentPlans.length || !isValidGatewatType) {
    return null;
  }

  return (
    <section className="checkout-form__section payment-options">
      <div className="form-card">
        <div className="form-card__header">
          <h3 className="form-card__heading">
            <Translation>{'checkout__payment_options'}</Translation>
          </h3>
        </div>
        <div className="form-card__content">
          {[defaultPaymentOption, ...paymentPlans].map((plan) => (
            <PaymentPlanOption
              key={plan.paymentPlanId}
              {...plan}
              currencyConfig={currencyConfig}
              onOpenDetails={onOpenDetails(plan.paymentPlanId)}
              onSelect={onPaymentPlanSelect(plan.paymentPlanId)}
              selected={selectedPaymentPlanId === plan.paymentPlanId}
            />
          ))}
        </div>
        <PaymentPlanModal
          open={!!openedPaymentPlan}
          onClose={onCloseDetails}
          currencyConfig={currencyConfig}
          {...openedPaymentPlan}
        />
      </div>
    </section>
  );
};
